import { parseQuerySegments } from './lib/parse-segments.js'

export interface HighlightPart {
  text: string
  match: boolean
}

/** Lower case without accents, so "cafe" finds "Café"; one character in, one out, so offsets carry over */
const fold = (text: string) =>
  [...text].map(char => char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase() || char).join('')

/**
 * The name, cut into marked and unmarked parts (#2555 M6). Search matches each query word as a prefix of a word, so a
 * mark covers that prefix at a word's start. `type:` and `#tag` segments filter; they are not marked.
 */
export const highlightParts = (text: string, query: string): HighlightPart[] => {
  const { cleanQuery } = parseQuerySegments(query)
  const words = (cleanQuery ?? '')
    .split(/\s+/)
    .map(word => fold(word))
    .filter(Boolean)
  if (words.length === 0) {
    return [{ text, match: false }]
  }
  const folded = fold(text)
  const marks = new Array<boolean>(text.length).fill(false)
  const wordStarts = [...folded.matchAll(/(?:^|[^\p{L}\p{N}])([\p{L}\p{N}])/gu)].map(
    m => (m.index ?? 0) + m[0].length - 1
  )
  for (const start of wordStarts) {
    const word = words.find(w => folded.startsWith(w, start))
    if (word) {
      marks.fill(true, start, start + word.length)
    }
  }
  const parts: HighlightPart[] = []
  ;[...text].forEach((char, index) => {
    const last = parts.at(-1)
    if (last && last.match === marks[index]) {
      last.text += char
    } else {
      parts.push({ text: char, match: marks[index] })
    }
  })
  return parts
}
